import { type KeyboardEvent, useId, useMemo, useRef, useState } from 'react';
import { Badge, type TagTone } from '../Badge/Badge';
import { Checkbox } from '../Checkbox/Checkbox';
import { cx } from '../cx';
import { ScrollEdge } from '../ScrollEdge/ScrollEdge';
import { SearchField } from '../SearchField/SearchField';
import { useIsDesktop } from '../useIsDesktop';

/**
 * A LIST OF OPTIONS THAT CAN BE TICKED, AND THE PRIMITIVE OWNS EVERY ROW OF IT
 * (`ui-foundation.md` D2), AND ITS BOUNDS (D10).
 *
 * Three call sites built this list by hand - the filter editor in two layouts, the record
 * page's multi-select input, the cell editor's - and each one re-derived how a row is
 * arranged. One of them got it wrong on 2026-09-05: `Checkbox` is `inline-flex`, so five
 * options dropped straight into a `<fieldset>` flowed like WORDS, three to a line, and the
 * next box painted over the tail of the previous label. Nothing in the gate could see it.
 *
 * So the arrangement lives here, once: ONE OPTION PER ROW, at the touch floor, with a
 * hairline between rows and a label that fills the rest of the row so the whole row is the
 * target. A caller says WHICH options and WHAT is ticked; it never says how a row is laid
 * out, and there is no layout or density prop.
 *
 * AND IT NEVER GROWS PAST ITS BOUND (D10, owner 2026-09-06). A list that adds a row per
 * option does not scale: twelve statuses are twelve rows, and a filter popover holding one
 * grows with every condition somebody adds. Up to six options every row is shown. From the
 * seventh the list is SIX ROWS TALL (eight in a sheet, which scrolls anyway), scrolls
 * inside itself, and gains a search field over the labels. The reference is the iOS
 * searchable checkmark list: search at the top, the rest a short scroller.
 *
 * THE SEARCH FIELD IS PINNED INSIDE THAT SCROLLER, AND IT IS THE SCROLL EDGE'S HOST (wave 5
 * round 1, C1-1). It is the one element these rows pass under, so it is the one Apple's
 * effect answers to; because it holds flow space, the first row starts below it without
 * anything declaring an inset, and the bound is six rows plus the field. See the comment at
 * the construction itself for the sources.
 *
 * AND THE TICK IS THE ONLY PLACE THE SELECTION LIVES (wave 4 round 2, finding 2). Until this
 * round every ticked option was ALSO a grey capsule with a remove cross above the search
 * field, which is a web token field: a second control for the value the checklist under it
 * already holds, in a vocabulary Apple's forms do not have. Apple's multi-select in a form is
 * the checklist alone, and a count, where one is wanted, is the group's footer sentence. The
 * Backspace shortcut that removed the last capsule went with them - a key that deletes a
 * selection nothing on the screen shows is a hidden destructive action, not a convenience.
 *
 * LONG LABELS WRAP RATHER THAN TRUNCATE. The state matrix has a 48-character label without
 * a space in a 240px container, and there are only two honest answers to it: cut the word
 * off, or break it. A tag name that reads "lifecycle_status_onbo..." tells the reader
 * nothing about which option they are ticking, so the row grows instead.
 */
export interface OptionListOption {
  readonly value: string;
  readonly label: string;
  /** A select-option colour. With one, the label renders as a `Badge` (the closed six). */
  readonly tone?: TagTone | undefined;
  readonly disabled?: boolean | undefined;
}

export interface OptionListProps {
  readonly options: readonly OptionListOption[];
  /** The ticked values. Order is the OPTIONS' order on the way out, never the click order. */
  readonly value: readonly string[];
  readonly onChange: (value: readonly string[]) => void;
  /** A visible caption above the list, which also names the group. */
  readonly legend?: string | undefined;
  /** Names the list where a visible legend would be a second label for the same thing. */
  readonly ariaLabel?: string | undefined;
  /** Everything is shown and nothing can be changed: a value somebody else may write. */
  readonly readOnly?: boolean | undefined;
  /** Shown in place of the list when there is nothing to tick. A sentence, never "Empty". */
  readonly emptyText?: string | undefined;
  /**
   * WHERE THIS LIST STANDS, which decides whether it carries a scroll edge effect (wave 3,
   * S-C2 and SH-A11, owner-57: "das verschwinden einfach verschwommen gemacht?").
   *
   * `chrome` is the default and is what every existing caller is: a popover, a sheet, a cell
   * editor, where the bands ARE the host's own material passing over the rows. `content` is a
   * list inside a form on the page's own ground, and it takes the canvas tint.
   *
   * WAVE 4, C2-20: THE PROP PICKS THE TINT, NOT WHETHER THERE IS AN EDGE. Wave 3 read the
   * HIG line "Only use a scroll edge effect when a scroll view is behind floating interface
   * elements" as "nothing floats over a list in a group box" and gave `content` no edge at
   * all. The owner saw the result on 2026-09-12 (owner-94) and it is the rows passing under
   * this list's own search field with nothing between them - so something does float over
   * it, the rule applies, and both grounds carry the effect.
   *
   * WAVE 5, C1-1: IT ALSO PICKS THE HOST'S NAME, `group-list` on `content` and `inner-list`
   * on `chrome`, because the review enumerates the effect's hosts by name and a list in a
   * settings group is the host check (u) reserved that word for.
   */
  readonly ground?: 'chrome' | 'content' | undefined;
  /**
   * PLACEMENT ONLY (D1): margin, width in a form row, `self-*`. Never padding, never
   * `space-*`, never a display or flow class - those are this component's own business,
   * and the bug above is what happens when a caller decides them.
   */
  readonly className?: string | undefined;
}

/**
 * The seventh option is where a list stops being a glance (D10). It is the count that
 * turns on BOTH the bound and the search, so a list can never be scrollable without a way
 * to jump past the scrolling.
 */
const SEARCH_FROM = 7;

/**
 * Six 40px rows on a desktop, eight in a sheet - which scrolls on its own anyway - PLUS THE
 * PINNED FIELD (wave 5 round 1, C1-1). A content inset does not shrink the scroller: in
 * UIKit the scroll view's frame runs UNDER the floating element and `contentInset` pushes the
 * content clear of it (doc:
 * https://developer.apple.com/documentation/uikit/uiscrollview/contentinsetadjustmentbehavior),
 * so the box grows by the field's own height and D10's bound still shows the rows it promised.
 *
 * AND THE FIELD'S HEIGHT IS A TOKEN THE PRODUCT ALREADY HAS: `ui/Input` sets its floor with
 * `min-h-(--touch-target-min)` (`ui/Input/Input.tsx`), which is what the pinned row measures.
 * Wave 4 spent `--scroll-edge-overhang` here; A deleted that token with the five hand-made
 * bands it drew (f537fcf), and a `calc()` naming a token nothing declares is invalid at
 * computed-value time - which is why this cap read `max-height: none` on main and the list
 * drew all 34 of its rows.
 */
const ROWS_DESKTOP = 'max-h-[calc(15rem_+_var(--touch-target-min))]';
const ROWS_SHEET = 'max-h-[calc(20rem_+_var(--touch-target-min))]';

export function OptionList({
  options,
  value,
  onChange,
  legend,
  ariaLabel,
  readOnly = false,
  emptyText = 'This field has no options yet.',
  ground = 'chrome',
  className,
}: OptionListProps) {
  const listId = useId();
  const desktop = useIsDesktop();
  const [query, setQuery] = useState('');
  /** Which row the arrow keys are on. A VALUE, so a re-filter cannot move it silently. */
  const [activeValue, setActiveValue] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const rowRefs = useRef(new Map<string, HTMLButtonElement | null>());

  const bounded = options.length >= SEARCH_FROM;
  /** `ui/ScrollEdge` names the page's ground `canvas`; a form's list stands on it. */
  const edgeGround = ground === 'chrome' ? 'chrome' : 'canvas';
  /**
   * THE NAME THE REVIEW ENUMERATES THIS HOST BY (`ui/ScrollEdge`'s `host` prop). Check (u)
   * in `packages/e2e/review/behaviour.spec.ts` reserved `group-list` for "a bounded list
   * inside a settings group", which is exactly what `ground="content"` is; a list standing
   * on a popover, a sheet or a cell editor keeps the name it has had, `inner-list`.
   */
  const edgeHost = ground === 'chrome' ? 'inner-list' : 'group-list';
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, query]);

  const name = legend ?? ariaLabel ?? 'options';
  const activeIndex = Math.max(
    matches.findIndex((option) => option.value === activeValue),
    0,
  );

  /**
   * The new selection, in the OPTIONS' order. Both multi-select inputs already kept that
   * rule and the filter editor appended in click order; one primitive cannot hold two
   * answers, and option order is the one a reader can see for themselves.
   */
  const toggle = (optionValue: string, checked: boolean): void => {
    onChange(
      options
        .filter((option) => (option.value === optionValue ? checked : value.includes(option.value)))
        .map((option) => option.value),
    );
  };

  const focusRow = (index: number): void => {
    const next = matches[index];
    if (next === undefined) return;
    setActiveValue(next.value);
    rowRefs.current.get(next.value)?.focus();
  };

  /**
   * ESCAPE CLEARS THE SEARCH FIRST AND CLOSES THE PANEL SECOND (D10). This list does not
   * own a panel and cannot close one, so what it owns is whether the key KEEPS TRAVELLING:
   * swallowed while there is a search to clear, passed on when there is not.
   *
   * It hangs off the two elements that can HAVE the focus - the search field and the list -
   * rather than off a wrapper, because a keydown handler on a plain `<div>` is an
   * interactive element without a role, which is both a lint error and true.
   */
  const swallowedEscape = (event: KeyboardEvent<HTMLElement>): boolean => {
    if (event.key !== 'Escape' || query === '') return false;
    event.preventDefault();
    event.stopPropagation();
    setQuery('');
    return true;
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    if (swallowedEscape(event)) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusRow(Math.min(activeIndex + 1, matches.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusRow(Math.max(activeIndex - 1, 0));
      return;
    }
    // TYPING GOES TO THE SEARCH FIELD, and Space is not typing: it is what ticks the row
    // the arrow keys are on, which the checkbox under focus already handles.
    if (
      bounded &&
      event.key.length === 1 &&
      event.key !== ' ' &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      event.preventDefault();
      setQuery((current) => `${current}${event.key}`);
      searchRef.current?.focus();
    }
  };

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    swallowedEscape(event);
  };

  const rows = (
    <ul
      id={listId}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      onKeyDown={onListKeyDown}
      className="divide-y divide-line-subtle"
    >
      {matches.map((option, index) => {
        const disabled = readOnly || option.disabled === true;
        const controlId = `${listId}-${option.value}`;
        const active = index === activeIndex;
        return (
          <li
            key={option.value}
            className="flex min-h-(--touch-target-min) min-w-0 items-center gap-2.5"
          >
            <Checkbox
              id={controlId}
              className="shrink-0"
              checked={value.includes(option.value)}
              onCheckedChange={(checked) => toggle(option.value, checked)}
              disabled={disabled}
              ariaLabel={option.label}
              // ONE TAB STOP FOR THE LIST (D10): the arrow keys move inside it, so forty
              // options are one stop on the way to the button below them, not forty.
              tabIndex={active ? 0 : -1}
              ref={(node) => {
                rowRefs.current.set(option.value, node);
              }}
            />
            {/*
              The label is the REST OF THE ROW, so the target is the row rather than a
              20px box, and `htmlFor` is what makes that true for a Radix checkbox -
              which renders a <button>, so a wrapping label would not forward the click.
            */}
            <label
              htmlFor={controlId}
              className={cx(
                'min-w-0 flex-1 py-1.5 text-sm break-words text-ink',
                disabled && 'opacity-50',
              )}
            >
              {option.tone === undefined ? (
                option.label
              ) : (
                <Badge tone={option.tone}>{option.label}</Badge>
              )}
            </label>
          </li>
        );
      })}
    </ul>
  );

  const body =
    options.length === 0 ? (
      <p className="py-2 text-sm text-ink-muted">{emptyText}</p>
    ) : (
      <div className="min-w-0 space-y-2">
        {bounded ? (
          /**
           * THE SEARCH FIELD IS THE HOST, AND IT STANDS INSIDE THE SCROLLER (wave 5 round 1,
           * C1-1; owner-94, "Apple hat doch da Liquid Glass, was ineinander uebergeht").
           *
           * Apple's effect is bounded by a REAL element and sits only where a scroll view is
           * behind a floating one: "Only use a scroll edge effect when a scroll view is
           * behind floating interface elements. Scroll edge effects aren't decorative."
           * (hig: https://developer.apple.com/design/human-interface-guidelines/scroll-views;
           * soft is the default, wwdc: https://developer.apple.com/videos/play/wwdc2025/356/
           * at 11:18, and effects are never stacked, at 11:50.) The floating element over
           * these rows is this list's own search field, so the field is the host: pinned at
           * the scroller's top, carrying the effect at exactly its own height.
           *
           * WHAT THAT BUYS, AND IT IS THE REASON FOR THE MOVE. Wave 4 mounted two zero-height
           * wrappers beside the scroller and made both the band and the content inset out of
           * `--scroll-edge-overhang`; A deleted that token with the hand-made bands it drew
           * (f537fcf), and the list lost its bound, its inset and its band in one go. A
           * pinned element occupies flow space, so the inset is now the host's own height BY
           * LAYOUT - which is what `contentInset` does in UIKit (doc:
           * https://developer.apple.com/documentation/uikit/uiscrollview/contentinsetadjustmentbehavior)
           * - and there is nothing left to declare.
           *
           * AND THERE IS NO BOTTOM BAND. Nothing floats over the list's lower edge; the rows
           * clip at the group box's hairline, and an effect drawn there would be the
           * decoration the HIG's sentence above refuses.
           */
          <div
            data-bounded-list={ground}
            className={cx(
              'overflow-y-auto overscroll-contain',
              desktop ? ROWS_DESKTOP : ROWS_SHEET,
            )}
          >
            {/*
              `sticky` is what pins it, `isolate` keeps the effect's `z-index: -1` behind the
              field and in front of the rows, and the host is positioned - which is what
              `ui/ScrollEdge` asks of a host and what check (t) reads as `offsetParent`.
            */}
            <div className="sticky top-0 isolate">
              <ScrollEdge host={edgeHost} style="soft" ground={edgeGround} />
              {/* THE SEARCH SHELL, NOT A BARE INPUT (wave 5 round 5, the owner's correction
                  C6-1). This list is where he met the defect - the webhook pane's Events and
                  Fields groups - and the cause was that the shell was never used here: a raw
                  `Input` paints the field's fill and can carry no rim, because no engine
                  renders a pseudo-element on a replaced element. `SearchField` is the label
                  the kit's capsule needs, and `content` is the place this list stands in. */}
              <SearchField
                ref={searchRef}
                context="content"
                label={`Search ${name}`}
                placeholder="Search"
                value={query}
                aria-controls={listId}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onSearchKeyDown}
              />
            </div>
            {matches.length === 0 ? (
              <p className="px-1 py-6 text-center text-sm text-ink-muted">
                Nothing here matches what you typed.
              </p>
            ) : (
              rows
            )}
          </div>
        ) : (
          rows
        )}
      </div>
    );

  /**
   * A `<fieldset>` ONLY WHEN THERE IS A LEGEND TO PUT IN IT. An unnamed group is furniture:
   * it draws a box a screen reader announces and cannot say what is in. Where the list has
   * no caption its host has already named it - the cell editor's panel says "Edit Roles of
   * Mila Vance", the record row prints the field's label beside the control - and `ariaLabel`
   * puts that name on the list itself.
   */
  return legend === undefined ? (
    <div className={cx('min-w-0', className)}>{body}</div>
  ) : (
    <fieldset className={cx('min-w-0', className)}>
      <legend className="text-xs text-ink-muted">{legend}</legend>
      {body}
    </fieldset>
  );
}
